"use client"

import { ReferralData } from "@nelo/signup-lib"
import { sendGTMEvent } from "@next/third-parties/google"
import { useSearchParams } from "next/navigation"
import React, { useEffect, useMemo } from "react"

import { PSEUDO_ID_COOKIE } from "@lib/constants"
import { getSessionCookie } from "@lib/util/get-session-cookie-client"
import { retrieveUTMParams } from "@lib/util/utm-params"
import { sendGoogleAnalyticsEvent } from "@modules/common/utils/analytics"
import { useLoanApplicationStatus } from "@modules/signup-and-order-flow/util/use-check-loan-application"
import { useClientInitialization } from "@modules/signup-and-order-flow/util/use-client-initializations"
import useProductData from "@modules/signup-and-order-flow/util/use-fetch-product-data"
import { useGetCalculatedPrices } from "@modules/signup-and-order-flow/util/use-get-product-details"
import { RegionInfo } from "types/global"

import { resolveBackAction } from "./back-navigation-resolver"
import { useSession } from "./session-context"
import { stepRegistry, StepId, StepProps } from "./step-registry"
import { resolveCurrentStep } from "./step-resolver"

interface SignupAndOrderFlowStepsProps {
  onCloseSheet: () => void
}

const SignupAndOrderFlowSteps = ({ onCloseSheet }: SignupAndOrderFlowStepsProps) => {
  const { sessionData, updateSession } = useSession()

  const searchParams = useSearchParams()
  const productId = searchParams.get("productId")
  const variantId = searchParams.get("variantId")

  const { product, discounts, region, selectedVariant } = useProductData(productId)
  const calculatedPrices = useGetCalculatedPrices(product, selectedVariant, region as RegionInfo, discounts)

  const { isInitialized } = useClientInitialization()
  const loanApplicationStatus = useLoanApplicationStatus(isInitialized, sessionData.progress.signupCompleted)

  useEffect(() => {
    if (calculatedPrices && calculatedPrices !== sessionData.product.calculatedPrices) {
      updateSession({ product: { ...sessionData.product, calculatedPrices, productId } })
    }
  }, [calculatedPrices, updateSession])

  const initialReferralData = useMemo((): ReferralData => {
    const utmParams = retrieveUTMParams()
    const pseudoId = getSessionCookie(PSEUDO_ID_COOKIE)
    const url = typeof window !== "undefined" ? window.location.href.toString() : undefined
    return {
      source: utmParams?.utm_source,
      medium: utmParams?.utm_medium,
      campaign: utmParams?.utm_campaign,
      term: utmParams?.utm_term,
      ad: utmParams?.utm_ad,
      content: utmParams?.utm_content,
      utmId: utmParams?.utm_id,
      // url: url,
      productId: productId || undefined,
      variantId: selectedVariant?.id,
      isMarketplace: true,
      gclid: utmParams?.utm_gclid,
      marketplaceCheckoutType: "web_native",
      productHandle: product?.handle || undefined,
      storefrontSessionId: pseudoId || undefined,
    }
  }, [productId, selectedVariant, product])

  useEffect(() => {
    if (loanApplicationStatus !== null && loanApplicationStatus !== sessionData.loan.applicationStatus) {
      updateSession({ loan: { ...sessionData.loan, applicationStatus: loanApplicationStatus } })
    }
  }, [loanApplicationStatus, updateSession])

  const currentStepId = resolveCurrentStep(sessionData)

  // Track step transitions
  useEffect(() => {
    sendGoogleAnalyticsEvent("SIGNUP_AND_ORDER_FLOW_STEP", {
      step: currentStepId,
      checkoutType: "web_native",
    })
  }, [currentStepId])

  const handleStepComplete = () => {
    if (currentStepId === StepId.SIGNUP_NATIVE) {
      sendGTMEvent({
        event: "signup_success",
        sessionId: sessionData.user.userUuid,
        productId,
        variantId,
      })
      sendGoogleAnalyticsEvent("SIGNUP_SUCCESS", {
        flow_type: "web_native",
      })
    }
  }

  const StepComponent = stepRegistry[currentStepId]

  // Handle back navigation
  const handleBack = () => {
    const backAction = resolveBackAction(currentStepId, sessionData)

    if (backAction.type === "CLOSE_SHEET") {
      onCloseSheet()
    } else if (backAction.type === "NAVIGATE_TO_STEP") {
      if (backAction.stepId === StepId.PAYMENT_OPTIONS_PICKER) {
        updateSession({ progress: { ...sessionData.progress, paymentOptionsCompleted: false } })
      }
    }
  }

  // If no component found, show error
  if (!StepComponent) {
    return <div>Error: No component found for step {currentStepId}</div>
  }

  // Prepare props for the step component
  const stepProps: StepProps = {
    onComplete: handleStepComplete,
    onBack: handleBack,
    initialReferralData: initialReferralData,
  }

  return (
    <div>
      <StepComponent {...stepProps} />
    </div>
  )
}

export default SignupAndOrderFlowSteps
